export interface SseEvent {
  event: string;
  data: string;
  id?: string;
}

/**
 * Incremental parser for `text/event-stream` frames (WHATWG EventSource algorithm):
 * `event:`, `data:` (multi-line, joined by `\n`), `id:`, `retry:` (ignored) and `:` comments.
 * A frame is dispatched on the first empty line when at least one `data:` line was seen.
 */
export function createSseParser(onEvent: (evt: SseEvent) => void): { push(chunk: string): void; flush(): void } {
  let buffer = '';
  let eventName = '';
  let dataLines: string[] = [];
  let lastId: string | undefined;
  let hasData = false;

  const dispatch = (): void => {
    if (hasData) {
      const evt: SseEvent = { event: eventName || 'message', data: dataLines.join('\n') };
      if (lastId !== undefined) evt.id = lastId;
      onEvent(evt);
    }
    eventName = '';
    dataLines = [];
    hasData = false;
  };

  const handleLine = (line: string): void => {
    if (line === '') {
      dispatch();
      return;
    }
    if (line.startsWith(':')) return;
    const colon = line.indexOf(':');
    const field = colon === -1 ? line : line.slice(0, colon);
    let value = colon === -1 ? '' : line.slice(colon + 1);
    if (value.startsWith(' ')) value = value.slice(1);
    switch (field) {
      case 'event':
        eventName = value;
        break;
      case 'data':
        dataLines.push(value);
        hasData = true;
        break;
      case 'id':
        if (!value.includes('\0')) lastId = value;
        break;
      default:
        break;
    }
  };

  return {
    push(chunk: string) {
      buffer += chunk;
      for (let index = buffer.search(/\r\n|\r|\n/); index !== -1; index = buffer.search(/\r\n|\r|\n/)) {
        // A trailing '\r' may be the first half of a '\r\n' split across chunks: wait for more data.
        if (buffer[index] === '\r' && index === buffer.length - 1) break;
        const line = buffer.slice(0, index);
        const sepLength = buffer.startsWith('\r\n', index) ? 2 : 1;
        buffer = buffer.slice(index + sepLength);
        handleLine(line);
      }
    },
    flush() {
      if (buffer.length > 0) {
        handleLine(buffer.replace(/\r$/, ''));
        buffer = '';
      }
      dispatch();
    },
  };
}

/** Reads a streaming response body and feeds it to the parser until the stream ends or the signal aborts. */
export async function consumeSseBody(body: ReadableStream<Uint8Array> | null, onEvent: (evt: SseEvent) => void, signal?: AbortSignal): Promise<void> {
  const parser = createSseParser(onEvent);
  if (!body) return;
  const reader = body.getReader();
  const decoder = new TextDecoder();
  const abort = (): void => {
    void reader.cancel(signal?.reason).catch(() => undefined);
  };
  signal?.addEventListener('abort', abort, { once: true });
  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      parser.push(decoder.decode(value, { stream: true }));
    }
    parser.push(decoder.decode());
    parser.flush();
  } finally {
    signal?.removeEventListener('abort', abort);
  }
}
